import { useMsal } from '@azure/msal-react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, mergeClasses, Option, Text } from '@fluentui/react-components';
import { AttachFilled, DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { userResourceDocumentConfiguration } from '../../../configurations';
import { DocumentGroupTypeCode, DocumentGroupTypeCodes, ProgramDocument, validDocumentGroupTypeKeys } from '../../../models';
import { fileUploadValidator } from '../../../shared/functions';
import { UploadButton, ValidationMessageList } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';
import { FormInput } from '../../shared-components/form/FormInput';
import { SelectOptions } from '../../shared-components/form/SelectOptions';
import { LoadingSpinner } from '../../shared-components/LoadingSpinner';
import './AddOrEditProgramDocumentModal.scss';

interface AddOrEditProgramDocumentModalProps {
  isModalOpen: boolean;
  selectedProgramDocument?: ProgramDocument;
  onSuccess: () => void;
  onDismissModal: () => void;
}

type LoadingType = 'load' | 'add' | 'update';

const AddOrEditProgramDocumentModalImpl: React.FC<AddOrEditProgramDocumentModalProps> = (props) => {
  const {
    isModalOpen,
    selectedProgramDocument,
    onSuccess,
    onDismissModal,
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    userResourcesPageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();

  const [loadingType, setLoadingType] = useState<LoadingType>();
  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);
  const [programDocumentInputKey, setProgramDocumentInputKey] = useState<string>('');
  const [programDocumentUploadFileErrorMessages, setProgramDocumentUploadFileErrorMessages] = useState<string[]>([]);

  const loadingText = (): string => {
    if (loadingType === 'load') return 'Loading Program Document';
    if (loadingType === 'add') return 'Adding Program Document';
    if (loadingType === 'update') return 'Updating Program Document';
    return '';
  };

  const uploadProgramDocumentFile = async () => {
    if (!userResourcesPageUiStore.selectedFile || !userResourcesPageUiStore.programDocumentRequestBody) return;

    const fileLocationUrl = await apiStore.uploadProgramDocumentContent(instance, userResourcesPageUiStore.selectedFile);

    userResourcesPageUiStore.setProgramDocumentRequestBody({
      key: 'fileLocationUrl',
      value: fileLocationUrl,
    });

    userResourcesPageUiStore.setSelectedFile(undefined);
  };

  const handleDocumentGroupTypeChange = (newValue?: DocumentGroupTypeCode): void => {
    userResourcesPageUiStore.setProgramDocumentRequestBody({
      key: 'documentGroupTypeCode',
      value: newValue,
    });

    userResourcesPageUiStore.setProgramDocumentRequestBody({
      key: 'winnerRole',
      value: newValue === 'winner-resource' ? 'Winner' : undefined,
    });

    userResourcesPageUiStore.setProgramDocumentRequestBody({
      key: 'awardType',
      value: newValue === 'award-resource' ? 'Category' : undefined,
    });

    if (!newValue) return;

    const currentResourcesLength = userResourcesPageUiStore.currentProgramDocuments(newValue).length;
    const nextOrdinal = currentResourcesLength + 1;

    userResourcesPageUiStore.setProgramDocumentRequestBody({
      key: 'ordinal',
      value: nextOrdinal,
    });
  };

  const handleDismissModal = () => {
    onDismissModal();
    setIsBeingValidated(false);
  };

  const handleSelectedFile = async (files: FileList) => {
    programDocumentInputKey === files[0].name ? setProgramDocumentInputKey('') : setProgramDocumentInputKey(files[0].name);
    if (!files || !files[0] || files.length > 1) return;

    const file = files[0];
    const validationResult = fileUploadValidator(file, userResourceDocumentConfiguration);
    if (!validationResult.isValid) {
      setProgramDocumentUploadFileErrorMessages(validationResult.errorMessages);
      return;
    }

    setProgramDocumentUploadFileErrorMessages([]);
    userResourcesPageUiStore.setSelectedFile(file);
  };

  const clearSelectedFile = () => {
    setProgramDocumentUploadFileErrorMessages([]);
    userResourcesPageUiStore.setSelectedFile(undefined);
  };

  const clearUploadedFile = () => {
    userResourcesPageUiStore.setProgramDocumentRequestBody({
      key: 'fileLocationUrl',
      value: undefined,
    });
  }

  const onAddProgramDocumentButtonClicked = async () => {
    setIsBeingValidated(true);
    if (!userResourcesPageUiStore.programDocumentRequestBody) return;
    if (userResourcesPageUiStore.isProgramDocumentRequestBodyValid) {
      setLoadingType('add');
      try {
        await uploadProgramDocumentFile();
        await apiStore.createProgramDocument(instance, userResourcesPageUiStore.programDocumentRequestBody);

        onSuccess();
        onDismissModal();
      } catch (error) {
        // TODO - Handle error
      } finally {
        setLoadingType(undefined);
      }
    }
  };

  const onUpdateProgramDocumentButtonClicked = async () => {
    setIsBeingValidated(true);
    if (!userResourcesPageUiStore.programDocumentRequestBody || !selectedProgramDocument) return;
    if (userResourcesPageUiStore.isProgramDocumentRequestBodyValid) {
      setLoadingType('update');
      try {
        await uploadProgramDocumentFile();
        await apiStore.updateProgramDocument(instance, selectedProgramDocument.programDocumentId, userResourcesPageUiStore.programDocumentRequestBody);

        onSuccess();
        onDismissModal();
      } catch (error) {
        // TODO - Handle error
      } finally {
        setLoadingType(undefined);
      }
    }
  };

  const preloadData = () => {
    setLoadingType('load');

    if (selectedProgramDocument) {
      userResourcesPageUiStore.createProgramDocumentRequestBodyForUpdate(selectedProgramDocument);
    } else {
      userResourcesPageUiStore.createProgramDocumentRequestBody();
    }

    setLoadingType(undefined);
  };

  const renderUploadedFile = (fileName: string, onRemovingFile: () => void) => (
    <FlexBox
      aligns='start'
      className='uploaded-file'
    >
      <AttachFilled fontSize={18} />
      <Text>{fileName}</Text>
      <Button
        appearance='transparent'
        size='small'
        title='remove'
        icon={<DismissRegular fontSize={18} />}
        onClick={onRemovingFile}
      />
    </FlexBox>
  );

  useEffect(() => {
    preloadData();
    return () => {
      setIsBeingValidated(false);
      userResourcesPageUiStore.resetModalStates();
    };
  }, []);

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(_, data) => {
        if (!data.open && !loadingType) handleDismissModal();
      }}
    >
      <DialogSurface
        className={mergeClasses(commonStyles.modalSurface, 'add-or-edit-program-document-modal')}
      >
        <DialogBody
          className={commonStyles.modalBody}
        >
          <DialogTitle
            action={(
              <Button
                title='close'
                appearance='transparent'
                icon={<DismissRegular />}
                onClick={handleDismissModal}
              />
            )}
          >
            {selectedProgramDocument ? 'Update program document' : 'Add program document'}
          </DialogTitle>
          <DialogContent>
            {
              loadingType ? (
                <LoadingSpinner
                  label={loadingText()}
                />
              ) : (
                <FlexBox
                  direction='column'
                >
                  <SelectOptions
                    label='Resource type'
                    validationMessage={
                      isBeingValidated
                      && userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.documentGroupTypeCode?.length
                        ? (
                          <ValidationMessageList
                            messages={userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.documentGroupTypeCode}
                          />
                        )
                        : undefined
                    }
                    onOptionSelect={(data) => handleDocumentGroupTypeChange(data.optionValue as DocumentGroupTypeCode | undefined)}
                    dropdownProps={{
                      inlinePopup: true,
                      selectedOptions: [userResourcesPageUiStore.programDocumentRequestBody?.documentGroupTypeCode ?? ''],
                      value: userResourcesPageUiStore.programDocumentRequestBody?.documentGroupTypeCode ? DocumentGroupTypeCodes[userResourcesPageUiStore.programDocumentRequestBody.documentGroupTypeCode] : '',
                    }}
                  >
                    {
                      validDocumentGroupTypeKeys.map((key) => (
                        <Option
                          key={key}
                          value={key}
                        >
                          {DocumentGroupTypeCodes[key]}
                        </Option>
                      ))
                    }
                  </SelectOptions>
                  <FormInput
                    label='Display name'
                    validationMessage={
                      isBeingValidated
                      && userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.displayName?.length
                        ? (
                          <ValidationMessageList
                            messages={userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.displayName}
                          />
                        )
                        : undefined
                    }
                    hintMessage={{
                      children: `${userResourcesPageUiStore.charsLength['displayName']} / 50`,
                      className: commonStyles.inputCharCounterHint,
                    }}
                    onChange={(newValue) => userResourcesPageUiStore.setProgramDocumentRequestBody({
                      key: 'displayName',
                      value: newValue,
                    })}
                    inputProps={{
                      maxLength: 50,
                      value: userResourcesPageUiStore.programDocumentRequestBody?.displayName || '',
                    }}
                  />
                  <FormInput
                    label='Description'
                    validationMessage={
                      isBeingValidated
                      && userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.description?.length
                        ? (
                          <ValidationMessageList
                            messages={userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.description}
                          />
                        )
                        : undefined
                    }
                    hintMessage={{
                      children: `${userResourcesPageUiStore.charsLength['description']} / 200`,
                      className: commonStyles.inputCharCounterHint,
                    }}
                    onChange={(newValue) => userResourcesPageUiStore.setProgramDocumentRequestBody({
                      key: 'description',
                      value: newValue,
                    })}
                    textAreaProps={{
                      maxLength: 200,
                      value: userResourcesPageUiStore.programDocumentRequestBody?.description || '',
                    }}
                  />
                  <FormInput
                    label='Ordinal'
                    validationMessage={
                      isBeingValidated
                      && userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.ordinal?.length
                        ? (
                          <ValidationMessageList
                            messages={userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.ordinal}
                          />
                        )
                        : undefined
                    }
                    onChange={(value) => userResourcesPageUiStore.setProgramDocumentRequestBody({
                      key: 'ordinal',
                      value: value === '' ? 0 : +value,
                    })}
                    inputProps={{
                      type: 'number',
                      className: 'input--ordinal',
                      value: `${userResourcesPageUiStore.programDocumentRequestBody?.ordinal ?? 1}`,
                    }}
                  />
                  <FlexBox
                    direction='column'
                    aligns='start'
                  >
                    <Text weight='semibold'>Resource file</Text>
                    <UploadButton
                      buttonProps={{
                        appearance: 'secondary',
                        children: ' Add file',
                        disabled: !!userResourcesPageUiStore.selectedFile || !!userResourcesPageUiStore.programDocumentRequestBody?.fileLocationUrl,
                        hidden: !!userResourcesPageUiStore.selectedFile || !!userResourcesPageUiStore.programDocumentRequestBody?.fileLocationUrl,
                      }}
                      onUpload={(fileList) => handleSelectedFile(fileList as FileList)}
                      acceptFiles={userResourceDocumentConfiguration.supportedFileExtensions.join(',')}
                    >
                      {userResourcesPageUiStore.selectedFile && renderUploadedFile(userResourcesPageUiStore.selectedFile.name, clearSelectedFile)}
                      {userResourcesPageUiStore.programDocumentRequestBody?.fileLocationUrl && renderUploadedFile(userResourcesPageUiStore.programDocumentRequestBody?.fileLocationUrl, clearUploadedFile)}
                    </UploadButton>
                    {
                      programDocumentUploadFileErrorMessages.length > 0 && (
                        programDocumentUploadFileErrorMessages.map((errorMessage, index) => (
                          <Text
                            key={index}
                            size={200}
                            className='text--red'
                            block
                          >
                            {errorMessage}
                          </Text>
                        ))
                      )
                    }
                    {
                      isBeingValidated && userResourcesPageUiStore.resourceFileErrorMessage && (
                        <Text
                          size={200}
                          className='text--red'
                          block
                        >
                          {userResourcesPageUiStore.resourceFileErrorMessage}
                        </Text>
                      )
                    }
                  </FlexBox>
                  {
                    userResourcesPageUiStore.programDocumentRequestBody?.documentGroupTypeCode === 'winner-resource' && (
                      <>
                        <SelectOptions
                          label='Role'
                          validationMessage={
                            isBeingValidated
                            && userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.winnerRole?.length
                              ? (
                                <ValidationMessageList
                                  messages={userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.winnerRole}
                                />
                              )
                              : undefined
                          }
                          onOptionSelect={(data) => userResourcesPageUiStore.setProgramDocumentRequestBody({
                            key: 'winnerRole',
                            value: data.optionValue || undefined,
                          })}
                          dropdownProps={{
                            inlinePopup: true,
                            value: userResourcesPageUiStore.programDocumentRequestBody?.winnerRole ?? '',
                            selectedOptions: [userResourcesPageUiStore.programDocumentRequestBody?.winnerRole ?? ''],
                          }}
                        >
                          <Option text='' value='' />
                          <Option value='Winner'>Winner</Option>
                          <Option value='Finalist'>Finalist</Option>
                          <Option value='Both'>Both</Option>
                        </SelectOptions>
                        <SelectOptions
                          label='Type of award'
                          validationMessage={
                            isBeingValidated
                            && userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.awardType?.length
                              ? (
                                <ValidationMessageList
                                  messages={userResourcesPageUiStore.programDocumentRequestValidation.fieldErrors.awardType}
                                />
                              )
                              : undefined
                          }
                          onOptionSelect={(data) => userResourcesPageUiStore.setProgramDocumentRequestBody({
                            key: 'awardType',
                            value: data.optionValue || undefined,
                          })}
                          dropdownProps={{
                            inlinePopup: true,
                            value: userResourcesPageUiStore.programDocumentRequestBody?.awardType ?? '',
                            selectedOptions: [userResourcesPageUiStore.programDocumentRequestBody?.awardType ?? ''],
                          }}
                        >
                          <Option text='' value='' />
                          <Option value='Category'>Category</Option>
                          <Option value='Country'>Country</Option>
                          <Option value='Both'>Both</Option>
                        </SelectOptions>
                      </>
                    )
                  }
                </FlexBox>
              )
            }
          </DialogContent>
          <DialogActions>
            {
              !selectedProgramDocument && (
                <Button
                  appearance='primary'
                  disabled={(isBeingValidated && !userResourcesPageUiStore.isProgramDocumentRequestBodyValid) || !!loadingType}
                  onClick={() => onAddProgramDocumentButtonClicked()}
                >
                  Add program document
                </Button>
              )
            }
            {
              selectedProgramDocument && (
                <Button
                  appearance='primary'
                  disabled={(isBeingValidated && !userResourcesPageUiStore.isProgramDocumentRequestBodyValid) || !!loadingType}
                  onClick={() => onUpdateProgramDocumentButtonClicked()}
                >
                  Update program document
                </Button>
              )
            }
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const AddOrEditProgramDocumentModal = observer(AddOrEditProgramDocumentModalImpl);
